import { useCallback } from 'react';
import { useTranslation } from 'react-i18next';

import { AxiosResponse, isAxiosError } from 'axios';

import { I18NextNS } from 'src/models/Language.model';

import useGetGalenErrorMsg from './useGetGalenErrorMsg';

const useHandleHttpRequestError = () => {
  const { getErrorMessage } = useGetGalenErrorMsg();

  const { t } = useTranslation(I18NextNS.COMMON);

  const extractErrorMessage = useCallback((data: string): string => {
    const errorMessageParts = data.split(' ');
    return errorMessageParts.length > 1 ? errorMessageParts[1] : data;
  }, []);

  const handleErrorResponse = useCallback(
    (response: AxiosResponse): string => {
      const { data } = response;
      return getErrorMessage(extractErrorMessage(data));
    },
    [extractErrorMessage, getErrorMessage],
  );

  const handleHttpRequestError = useCallback(
    (err: unknown): string => {
      if (!isAxiosError(err)) {
        if (err instanceof Error) {
          return `${t('bootbox.title.Error')}: ${err.message}`;
        }
        return `${err}`;
      }

      const { response, request, message } = err;

      if (response) {
        return handleErrorResponse(response);
      }

      if (request) {
        // Request was made but no response was received
        return t('message.error.networkError');
      }

      // Something happened in setting up the request that triggered an Error
      return `${t('bootbox.title.Error')}: ${message}`;
    },
    [handleErrorResponse, t],
  );

  return {
    handleHttpRequestError,
  };
};

export default useHandleHttpRequestError;
